/**
 * 屏幕档位 Hook —— JS 侧读取当前档位的唯一入口
 *
 * 与 useDensity 的分工见 constants/screenTier.ts 顶部说明：
 *   档位管结构（本 hook），密度管字号（useDensity）。
 *
 * 消费者应优先用 CSS 的 --tier-* 变量（styles/tier-tokens.css）；
 * 只有在必须用 JS 分支（如切换组件形态、决定是否渲染某块）时才用本 hook。
 *
 * 数据源是 constants/screenTier.ts 的模块级 store，
 * 全局只有一个 resize 监听（由 initScreenTier 建立），本 hook 不重复监听。
 */
import { useSyncExternalStore } from 'react';
import {
  getScreenTier,
  setScreenTierOverride,
  subscribeScreenTier,
  type ScreenTier,
} from '@/constants/screenTier';

export type { ScreenTier };

export interface UseScreenTierResult {
  /** 当前档位：B 超大屏 / A 常规笔记本 / C iPad / D 手机 */
  tier: ScreenTier;
  /** 超大屏档（≥1920）：可并排双栏、解锁隐藏列 */
  isUltraWide: boolean;
  /** 常规笔记本档（1280–1919）：基准档 */
  isLaptop: boolean;
  /** iPad 档（768–1279）：触控优先，禁用 hover-only 交互 */
  isTablet: boolean;
  /** 手机档（<768）：单列堆叠，仅保证可读可操作 */
  isMobile: boolean;
  /** 触控档（C/D）：控件与热区需放大到 ≥40px */
  isTouch: boolean;
  /** 设计走查：手动锁定档位，'auto' 恢复按视口自动判定 */
  setTierOverride: (tier: ScreenTier | 'auto') => void;
}

/** 服务端/首帧快照 —— 恒定返回基准档 A，避免水合抖动 */
function getServerSnapshot(): ScreenTier {
  return 'A';
}

export function useScreenTier(): UseScreenTierResult {
  const tier = useSyncExternalStore(
    subscribeScreenTier,
    getScreenTier,
    getServerSnapshot,
  );

  return {
    tier,
    isUltraWide: tier === 'B',
    isLaptop: tier === 'A',
    isTablet: tier === 'C',
    isMobile: tier === 'D',
    isTouch: tier === 'C' || tier === 'D',
    setTierOverride: setScreenTierOverride,
  };
}

export default useScreenTier;